<template>
  <div class="flex items-center justify-between border-t border-t-subtle bg-surface p-6" data-testid="page-product-details-footer">
    <p class="txt-headline-m">{{ formatPrice(totalPrice, locale, allVariantsFree) }}</p>
    <div class="flex gap-3">
      <template v-if="allowPurchasing">
        <QuantitySelector
          :count="orderItemState.count"
          :max="orderItemState.maximum"
          @update:quantity="(c) => $emit('update:quantity', c)"
        />

        <TooltipWrapper :key="tooltipEnabled ? 'with-content' : 'without-content'" class="">
          <template #trigger>
            <button
              v-if="!editMode"
              class="btn-primary-color-s"
              :disabled="!addToOrderEnabled || !quantityValid"
              @click="$emit('addToCart')"
            >
              {{ $t('FestivalShop.products.details.configure.add_to_order') }}
            </button>
            <button v-else class="btn-primary-color-s" :disabled="!updateOrderEnabled || !quantityValid" @click="$emit('updateOrder')">
              {{ $t('FestivalShop.products.details.configure.update_order') }}
            </button>
          </template>

          <template v-if="tooltipEnabled && !editMode" #content>
            <template v-if="!quantityValid && !editMode">{{ $t('FestivalShop.products.details.disabled_due_to_maximum') }}</template>
            <template v-else-if="!editMode">{{ $t('FestivalShop.products.details.disabled_not_configured') }}</template>
          </template>
        </TooltipWrapper>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { TooltipWrapper } from '@ramp106/omrjs-core-ui'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { formatPrice } from '@/components/FestivalShop/helpers'
import QuantitySelector from '@/components/FestivalShop/QuantitySelector.vue'
import { festivalShopConfig } from '@/config/festivalShop'
import { useLocaleStore } from '@/stores'

interface OrderItemState {
  count: number
  price: number
  deliveryFee?: number
  maximum?: number
}

const props = defineProps<{
  orderItemState: OrderItemState
  editMode: boolean
  addToOrderEnabled: boolean
  updateOrderEnabled: boolean
  allVariantsFree: boolean
}>()

defineEmits<{
  'update:quantity': [count: number]
  addToCart: []
  updateOrder: []
}>()

const { locale } = storeToRefs(useLocaleStore())

const totalPrice = computed(() => {
  const { price, count, deliveryFee } = props.orderItemState
  return price * count + (deliveryFee || 0)
})

const { allowPurchasing } = festivalShopConfig

const tooltipEnabled = computed(() => {
  return (!quantityValid.value && props.editMode) || (props.editMode ? !props.updateOrderEnabled : !props.addToOrderEnabled)
})
const quantityValid = computed(() => {
  return props.orderItemState.maximum != undefined ? props.orderItemState.count <= props.orderItemState.maximum : true
})
</script>
